<div class="database-admin">
  <h2>Database Administration
    <div class="toolbar">
      <button class="refresh-button" (click)="scanDatabase()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button class="bulk-edit-button" (click)="toggleBulkEditMode()" [class.active]="bulkEditMode">
        <mat-icon>{{ bulkEditMode ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
        {{ bulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit' }}
      </button>
    </div>
  </h2>

  <!-- Filters -->
  <div class="filters">
    <div class="filter-group">
      <label for="search">Search (PK/SK):</label>
      <input 
        id="search" 
        type="text" 
        [(ngModel)]="searchTerm" 
        (input)="onSearchChange()" 
        placeholder="Enter PK or SK to search..."
        class="search-input">
    </div>
    
    <div class="filter-group">
      <label for="status">Status Filter:</label>
      <select id="status" [(ngModel)]="statusFilter" (change)="onStatusFilterChange()" class="status-select">
        <option value="all">All Statuses</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="denied">Denied</option>
      </select>
    </div>
  </div>

  <!-- Loading overlay -->
  <div class="loading-overlay" *ngIf="loading">
    <div class="spinner"></div>
    <p>Loading...</p>
  </div>

  <!-- Results count -->
  <div class="results-info">
    <p>Showing {{ filteredDrawings.length }} of {{ allDrawings.length }} drawings</p>
    <div *ngIf="bulkEditMode" class="bulk-info">
      <span>{{ selectedDrawings.size }} selected</span>
      <button class="bulk-action-btn" (click)="selectAllVisible()" [disabled]="loading">Select All Visible</button>
      <button class="bulk-action-btn" (click)="deselectAll()" [disabled]="loading">Deselect All</button>
      <button class="bulk-action-btn primary" (click)="initiateBulkEdit()" [disabled]="selectedDrawings.size === 0 || loading">
        Bulk Edit Selected
      </button>
    </div>
  </div>

  <!-- Drawings table -->
  <div class="drawings-table-container" *ngIf="!loading">
    <table class="drawings-table">
      <thead>
        <tr>
          <th *ngIf="bulkEditMode" class="checkbox-col">
            <input type="checkbox" 
                   [checked]="selectedDrawings.size === filteredDrawings.length && filteredDrawings.length > 0"
                   [indeterminate]="selectedDrawings.size > 0 && selectedDrawings.size < filteredDrawings.length"
                   (change)="selectedDrawings.size === filteredDrawings.length ? deselectAll() : selectAllVisible()">
          </th>
          <th>PK (Username)</th>
          <th>SK (Timestamp)</th>
          <th>Date Created</th>
          <th>Status</th>
          <th>Approval Date</th>
          <th>Denial Date</th>
          <th>Cleared Date</th>
          <th *ngIf="!bulkEditMode">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let drawing of filteredDrawings" class="drawing-row" [class.selected]="isDrawingSelected(drawing)">
          <td *ngIf="bulkEditMode" class="checkbox-col">
            <input type="checkbox" 
                   [checked]="isDrawingSelected(drawing)"
                   (change)="toggleDrawingSelection(drawing)">
          </td>
          <td>{{ drawing.PK }}</td>
          <td>{{ drawing.SK }}</td>
          <td>{{ formatDate(drawing.SK) }}</td>
          <td>
            <span class="status-badge" [style.background-color]="getStatusColor(drawing.ApprovalStatus || 'pending')">
              {{ drawing.ApprovalStatus || 'pending' }}
            </span>
          </td>
          <td>{{ formatApprovalTimestamp(drawing.ApprovalTimestamp) }}</td>
          <td>{{ formatApprovalTimestamp(drawing.DenialTimestamp) }}</td>
          <td>{{ formatApprovalTimestamp(drawing.ClearedTimestamp) }}</td>
          <td class="actions" *ngIf="!bulkEditMode">
            <button class="action-btn edit-btn" (click)="editDrawing(drawing)" title="Edit">
              <mat-icon>edit</mat-icon>
            </button>
            <button class="action-btn delete-btn" (click)="confirmDelete(drawing)" title="Delete">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Edit Modal -->
  <div class="modal-overlay" *ngIf="editingDrawing" (click)="cancelEdit()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <h3>Edit Drawing</h3>
      <div class="edit-form">
        <div class="form-group">
          <label>PK (Username):</label>
          <input type="text" [(ngModel)]="editingDrawing.PK" readonly>
        </div>
        
        <div class="form-group">
          <label>SK (Timestamp):</label>
          <input type="text" [(ngModel)]="editingDrawing.SK" readonly>
        </div>
        
        <div class="form-group">
          <label>Approval Status:</label>
          <select [(ngModel)]="editingDrawing.ApprovalStatus">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Approval Timestamp:</label>
          <input type="number" [(ngModel)]="editingDrawing.ApprovalTimestamp" placeholder="Unix timestamp">
        </div>
        
        <div class="form-group">
          <label>Denial Timestamp:</label>
          <input type="number" [(ngModel)]="editingDrawing.DenialTimestamp" placeholder="Unix timestamp">
        </div>
        
        <div class="form-group">
          <label>Cleared Timestamp:</label>
          <input type="number" [(ngModel)]="editingDrawing.ClearedTimestamp" placeholder="Unix timestamp">
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="btn-secondary" (click)="cancelEdit()">Cancel</button>
        <button class="btn-primary" (click)="saveDrawing()" [disabled]="loading">Save Changes</button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div class="modal-overlay" *ngIf="showDeleteConfirm" (click)="cancelDelete()">
    <div class="modal-content delete-modal" (click)="$event.stopPropagation()">
      <h3>Confirm Delete</h3>
      <p>Are you sure you want to delete this drawing?</p>
      <div class="delete-info">
        <strong>PK:</strong> {{ showDeleteConfirm.PK }}<br>
        <strong>SK:</strong> {{ showDeleteConfirm.SK }}<br>
        <strong>Status:</strong> {{ showDeleteConfirm.ApprovalStatus || 'pending' }}
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" (click)="cancelDelete()">Cancel</button>
        <button class="btn-danger" (click)="deleteDrawing()" [disabled]="loading">Delete</button>
      </div>
    </div>
  </div>

  <!-- Bulk Edit Confirmation Modal -->
  <div class="modal-overlay" *ngIf="showBulkConfirm" (click)="cancelBulkEdit()">
    <div class="modal-content bulk-edit-modal" (click)="$event.stopPropagation()">
      <h3>Bulk Edit Confirmation</h3>
      <p>You are about to update <strong>{{ selectedDrawings.size }}</strong> drawings.</p>
      <p class="bulk-note">Only fields with values will be updated. Leave fields empty to keep existing values.</p>
      
      <div class="bulk-edit-form">
        <div class="form-group">
          <label>New Status:</label>
          <select [(ngModel)]="bulkStatus" class="bulk-status-select">
            <option value="">Keep existing status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
        </div>

        <div class="form-group">
          <label>Approval Timestamp (Unix timestamp):</label>
          <input type="number" 
                 [(ngModel)]="bulkApprovalTimestamp" 
                 placeholder="Leave empty to keep existing value"
                 class="bulk-input">
        </div>

        <div class="form-group">
          <label>Denial Timestamp (Unix timestamp):</label>
          <input type="number" 
                 [(ngModel)]="bulkDenialTimestamp" 
                 placeholder="Leave empty to keep existing value"
                 class="bulk-input">
        </div>

        <div class="form-group">
          <label>Cleared Timestamp (Unix timestamp):</label>
          <input type="number" 
                 [(ngModel)]="bulkClearedTimestamp" 
                 placeholder="Leave empty to keep existing value"
                 class="bulk-input">
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="btn-secondary" (click)="cancelBulkEdit()">Cancel</button>
        <button class="btn-primary" (click)="executeBulkEdit()" 
                [disabled]="loading || (bulkStatus === '' && !bulkApprovalTimestamp && !bulkDenialTimestamp && !bulkClearedTimestamp)">
          Update {{ selectedDrawings.size }} Drawings
        </button>
      </div>
    </div>
  </div>
</div>
